/*
 * File: index.tsx
 * Author: Austin Robinson
 * Date Created: 9/28/2024
 * Copyright (c) DrivingDoc 2024
 */
import { useEffect } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import Backdrop from '@mui/material/Backdrop';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Grid from '@mui/material/Grid';
import { QueryClientProvider, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DialogProps, useDialogs } from '@toolpad/core/useDialogs';
import { FormProvider, useForm } from 'react-hook-form';

import {
  createChargeMutation as createChargeMutationOptions,
  getChargeOptions,
  getChargeQueryKey,
  updateChargeMutation as updateChargeMutationOptions,
} from '@drivingdoc/api-client/generated/react-query';
import { Charge } from 'types/finance';

import DateModified from 'components/Dialogs/components/DateModified';
import CurrencyInput from 'components/Forms/CurrencyInput';
import DateInput from 'components/Forms/DateInput';
import TextInput from 'components/Forms/TextInput';

import { getApiErrorMessage } from 'utils/getApiErrorMessage';
import { getZodObjectDefaults } from 'utils/getZodObjectDefaults';

import FeedbackDialog from '../../FeedbackDialog';
import { BaseChargeFormSchema, ChargeFormSchema, ChargeFormValues } from './ChargeSchema';
import ChargeStatus from './components/ChargeStatus';
import ChargeSummary from './components/ChargeSummary';
import DiscountFieldArray from './components/DiscountFieldArray';

// interface ChargeDialogState {
//     // id: string;
//     // type: 'create' | 'update';
//     id: string | null;
// }

// interface ChargeDialogProps {
//   onClose?: () => void;
// }

// Creating a new charge
export type NewChargeDialogParams = {
  chargeId?: null | undefined;
  studentId: string; // required if no chargeId
};

// Editing an existing charge
export type UpdateChargeDialogParams = {
  chargeId: string;
  studentId?: never; // disallow studentId when editing existing charge
};

// Union for new or existing charge
export type OpenChargeDialogParams = NewChargeDialogParams | UpdateChargeDialogParams;

export default function ChargeDialog({ payload: dialogState, open, onClose }: DialogProps<OpenChargeDialogParams>) {
  // dialog state
  // const [dialogState, setDialogState] = useState<ChargeDialogState | null>(null);
  // const [dialogState, setDialogState] = useState<OpenChargeDialogParams | null>(
  //   null
  // );

  const dialogs = useDialogs();

  const queryClient = useQueryClient();
  const createChargeMutation = useMutation(createChargeMutationOptions());
  const updateChargeMutation = useMutation(updateChargeMutationOptions());
  const existingCharge = useQuery({
    ...getChargeOptions({
      path: {
        id: dialogState?.chargeId ?? '',
      },
    }),
    gcTime: 0,
    enabled: !!dialogState?.chargeId,
  });
  const existingChargeData = existingCharge.data as Charge | undefined;

  // indicate if the charge has payments
  const chargeHasPayments = existingChargeData ? existingChargeData.student_charge_payments?.length > 0 : false;

  const form = useForm<ChargeFormValues>({
    mode: 'all',
    resolver: zodResolver(ChargeFormSchema),
  });

  const { handleSubmit } = form;

  useEffect(() => {
    // Creating a new charge, reset the form with base defaults:
    if (!dialogState.chargeId) {
      form.reset({
        ...getZodObjectDefaults(BaseChargeFormSchema),
        id: null,
        student_user_id: dialogState.studentId,
      });
    }
  }, [dialogState, form]);

  // const openChargeDialog = (params: OpenChargeDialogParams) => {
  //   setDialogState(params);

  //   // Creating a new charge, reset the form with base defaults:
  //   if (!params.chargeId) {
  //     form.reset({
  //       ...getZodObjectDefaults(BaseChargeFormSchema),
  //       id: null,
  //       student_user_id: params.studentId,
  //     });
  //   }
  // };

  const handleClose = () => {
    // close the dialog
    // setDialogState(null);
    form.reset({ id: null, student_user_id: undefined });
    onClose?.();
  };

  const createCharge = async (data: ChargeFormValues) => {
    try {
      // show confirmation dialog
      const confirmed = await dialogs.confirm(
        'Are you sure you want to charge the student? Any excess payments will automatically be applied to unpaid charges.',
        {
          cancelText: 'Cancel',
          severity: 'error',
        },
      );

      // if user cancels, return
      if (!confirmed) {
        return;
      }

      // create the charge
      const result = await createChargeMutation.mutateAsync({
        body: data,
      });

      if (result) {
        // close the dialog
        handleClose();

        // invalidate the existing charge query
        queryClient.invalidateQueries({
          queryKey: ['charge', { id: dialogState?.chargeId }],
        });

        // show success message
        dialogs.open(FeedbackDialog, {
          icon: 'success',
          title: 'Charge Created',
        });
      }
    } catch (error) {
      dialogs.open(FeedbackDialog, {
        title: 'Error creating charge',
        message: getApiErrorMessage(error),
      });
    }
  };

  const updateCharge = async (data: ChargeFormValues) => {
    try {
      // show confirmation dialog
      const confirmed = await dialogs.confirm(
        'Are you sure you want to update the charge? Any excess payments will automatically be applied to unpaid charges.',
        {
          cancelText: 'Cancel',
        },
      );

      // if user cancels, return
      if (!confirmed) {
        return;
      }

      // update the charge
      if (!data.id) {
        throw new Error('Charge id is required');
      }

      const result = await updateChargeMutation.mutateAsync({
        body: data,
        path: {
          id: data.id,
        },
      });

      if (result) {
        // close the dialog
        handleClose();

        // invalidate the existing charge query
        if (dialogState?.chargeId) {
          queryClient.invalidateQueries({
            queryKey: getChargeQueryKey({
              path: {
                id: dialogState.chargeId,
              },
            }),
          });
        }

        // show success message
        dialogs.open(FeedbackDialog, {
          icon: 'success',
          title: 'Charge Updated',
        });
      }
    } catch (error) {
      dialogs.open(FeedbackDialog, {
        title: 'Error updating charge',
        message: getApiErrorMessage(error),
      });
    }
  };

  const onSubmit = (data: ChargeFormValues) => {
    // create or update the charge
    if (dialogState?.chargeId) {
      updateCharge(data);
    } else {
      createCharge(data);
    }
  };

  // update form values when the existing charge data is fetched
  useEffect(() => {
    // if the existing charge data was fetched and the form is not already set with the existing data
    if (existingCharge.isSuccess && existingChargeData && form.getValues().id !== existingChargeData.id) {
      // reset the form with the existing charge data
      form.reset({
        id: existingChargeData.id,
        student_user_id: existingChargeData.student_user_id,
        memo: existingChargeData.memo ?? '',
        description: existingChargeData.description,
        private_notes: existingChargeData.private_notes,
        amount: existingChargeData.amount,
        amount_due: existingChargeData.amount_due,
        amount_due_lock: existingChargeData.amount_due_lock,
        deposit: existingChargeData.deposit,
        deposit_due: existingChargeData.deposit_due,
        deposit_due_lock: existingChargeData.deposit_due_lock,
        student_charge_discounts: existingChargeData.student_charge_discounts.map((d) => ({
          id: d.id,
          discount_id: d.discount_id,
          order: d.order,
          type: d.type,
          amount: d.amount,
        })),
      });
      console.log('Reset form with existing charge data:', existingChargeData);
    }
  }, [existingCharge.isSuccess, existingChargeData, form]);

  useEffect(() => {
    // show an error dialog if there was an issue fetching the existing charge data
    if (existingCharge?.isError) {
      dialogs.open(FeedbackDialog, {
        title: 'Something went wrong while fetching the existing charge info.',
        message: existingCharge?.error?.message ?? '',
      });
    }
  }, [dialogs, existingCharge?.error?.message, existingCharge?.isError]);

  const ChargeDialog = (
    <QueryClientProvider client={queryClient}>
      <Dialog fullWidth open={open} onClose={handleClose}>
        <DialogTitle component="div" display="flex" justifyContent="space-between">
          {/* {dialogState?.type === 'create' ? 'Create New Charge' : 'Update Charge'} */}
          {dialogState?.chargeId ? 'Update Charge' : 'Create New Charge'}
          {existingChargeData && <ChargeStatus charge={existingChargeData} />}
        </DialogTitle>

        <form noValidate onSubmit={handleSubmit(onSubmit)}>
          <FormProvider {...form}>
            <DialogContent>
              <Grid container rowSpacing={3} columnSpacing={1}>
                {/* <Grid size={12}>
                                    <Typography>{form.getValues()?.id ?? 'No Id Set'}</Typography>
                                </Grid> */}

                <Grid size={12}>
                  <TextInput name="memo" label="Memo" autoComplete="off" disabled={chargeHasPayments} />
                </Grid>

                <Grid size={12}>
                  <TextInput
                    name="description"
                    label="Description"
                    multiline
                    helperText={<span style={{ color: 'red' }}>Description visible to customer.</span>}
                    disabled={chargeHasPayments}
                  />
                </Grid>

                <Grid size={12}>
                  <TextInput
                    name="private_notes"
                    label="Private Notes"
                    multiline
                    helperText="Private notes are only visible to administrators."
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 4 }}>
                  <CurrencyInput
                    name="amount"
                    label="Total Amount"
                    textFieldProps={{
                      autoComplete: 'off',
                      disabled: chargeHasPayments,
                    }}
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 4 }}>
                  <DateInput name="amount_due" label="Total Amount Due Date" />
                </Grid>

                <Grid size={{ xs: 12, md: 4 }}>
                  <DateInput name="amount_due_lock" label="Account Lock Date" helperText="When Total Amount Overdue" />
                </Grid>

                <Grid size={{ xs: 12, md: 4 }}>
                  <CurrencyInput
                    name="deposit"
                    label="Deposit"
                    textFieldProps={{
                      autoComplete: 'off',
                      disabled: chargeHasPayments,
                    }}
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 4 }}>
                  <DateInput name="deposit_due" label="Deposit Due Date" />
                </Grid>

                <Grid size={{ xs: 12, md: 4 }}>
                  <DateInput name="deposit_due_lock" label="Account Lock Date" helperText="When Deposit Overdue" />
                </Grid>

                <Grid size={12}>
                  <DiscountFieldArray name="student_charge_discounts" />
                </Grid>

                <Grid size={12}>
                  <ChargeSummary />
                </Grid>

                <Grid size={12}>
                  <Box display="flex" flexDirection="column" rowGap={1}>
                    <DateModified
                      prefix="Created: "
                      date={existingChargeData?.created_at}
                      user={existingChargeData?.created_by_user}
                    />
                    <DateModified
                      prefix="Updated: "
                      date={existingChargeData?.updated_at}
                      user={existingChargeData?.updated_by_user}
                    />
                    <DateModified
                      prefix="Voided: "
                      date={existingChargeData?.voided_at}
                      user={existingChargeData?.voided_by_user}
                    />
                  </Box>
                </Grid>
              </Grid>
            </DialogContent>

            <DialogActions
              sx={{
                flexWrap: 'wrap',
                rowGap: 2,
              }}
            >
              <Button onClick={handleClose}>Cancel</Button>
              <Button type="submit">{dialogState?.chargeId ? 'Update' : 'Create'}</Button>
            </DialogActions>
          </FormProvider>
        </form>
        <Backdrop
          sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
          open={existingCharge.isLoading || createChargeMutation.isPending || updateChargeMutation.isPending}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      </Dialog>
    </QueryClientProvider>
  );

  return ChargeDialog;
}
